import React from 'react';
import { Navigate } from 'react-router-dom';
import { authRouteConfig } from './AuthRoutes';
import Error403 from '../../modules/errorPages/Error403';
import { errorPagesConfigs } from './ErrorPagesRoutes';
import { adminPagesConfigs } from './Admin';
import studentPagesConfigs from './Student';
import parentPagesConfigs from './Parent';
import { coachPagesConfigs } from './Coach';
import { initialUrl } from '@projectK/constants/AppConst';
import { useAuthUser } from '../../../../../libs/hooks/src/AuthHooks';
import { useApplicationSettings } from '@projectK/context/VerbiageContextProvider';
import { useAddonsSettings } from '@projectK/context/CheckAdddons';
import { tournamentAddon } from '@projectK/constants/AppEnums';
import {
  isSuperAdmin,
  isCoach,
  isParent,
  isStudent,
  isTournamentCoach,
  isAdmin,
  useRolePermissions,
} from '@projectK/context/RolesProvider';
import { useMobileSettings } from '@projectK/context/MobileContextProvider';
import {
  getTournamentWidgetReturnPath,
  shouldResumeTournamentRegistrationAfterLogin,
} from '@projectK/modules/ecommerce/TournamentDetail';

const getStudentOrParentHomePath = (user, isMobile) =>
  isMobile ? `/${user.roleMenuFlag[0]}/events` : '/dashboard';

const isTournamentWidgetPath = () =>
  typeof window !== 'undefined' &&
  window.location.pathname.match(/^\/widgets\/tournament\/[^/]+/);

const isRestrictedFromTournamentWidget = (user) =>
  isStudent(user) || isParent(user) || isCoach(user);

const getUrlPartIndices = (parts) => ({
  last: parts.length - 1,
  prev: parts.length - 2,
  prev2: parts.length - 3,
  prev3: parts.length - 4,
});

const resolveDeepLinkUrl = (slug, parts) => {
  const { last, prev, prev2, prev3 } = getUrlPartIndices(parts);

  if (slug && parts[prev] === 'products') {
    return `/product-details/${slug}`;
  }
  if (slug && parts[prev] === 'packages') {
    return `/package-details/${slug}`;
  }
  if (parts[prev2] === 'purchasePackage') {
    return `/purchasePackage/${parts[prev]}/${parts[last]}`;
  }
  if (parts[prev2] === 'addtocart' && parts[prev3] === 'purchasePackage') {
    return `/purchasePackage/addtocart/${parts[prev]}/${parts[last]}`;
  }
  if (parts[prev2] === 'addtocart' && parts[prev3] === 'product') {
    return `/product/addtocart/${parts[prev]}/${parts[last]}`;
  }
  if (parts[prev] === 'order_invoice') {
    return '/transaction-history';
  }
  if (parts[last] === 'unsubscribe-email') {
    return '/user/unsubscribe';
  }
  return null;
};

const resolveTournamentWidgetUrl = ({ user, isMobile, defaultUrl }) => {
  if (user && isTournamentWidgetPath()) {
    return isRestrictedFromTournamentWidget(user)
      ? getStudentOrParentHomePath(user, isMobile)
      : window.location.pathname;
  }

  const tournamentWidget = getTournamentWidgetReturnPath();
  if (
    tournamentWidget &&
    shouldResumeTournamentRegistrationAfterLogin() &&
    user &&
    !isRestrictedFromTournamentWidget(user)
  ) {
    return `/${tournamentWidget}`;
  }

  return defaultUrl;
};

const resolveInitialNewUrl = ({ defaultUrl, user, isMobile, slug, parts }) =>
  resolveDeepLinkUrl(slug, parts) ??
  resolveTournamentWidgetUrl({ user, isMobile, defaultUrl });

const getCurrentUrlParts = () => {
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '';
  const segments = pathname.split('/').filter(Boolean);
  return {
    parts: ['', ...segments],
    slug: segments[segments.length - 1] || '',
  };
};

if (typeof window !== 'undefined') {
  const fullUrl = window.location.href;
  const parts = fullUrl.split('/');
  if (parts?.includes('purchasePackage')) {
    localStorage.setItem('purchasePackage', 'purchasePackage');
    localStorage.setItem('packageId', parts[parts.length - 1]);
    localStorage.setItem('purchaseId', parts[parts.length - 2]);
  }
  if (parts?.includes('purchasePackage') && parts?.includes('addtocart')) {
    localStorage.setItem('purchasePackage', 'addtocart');
    localStorage.setItem('packageId', parts[parts.length - 1]);
    localStorage.setItem('purchaseId', parts[parts.length - 2]);
  }
  if (parts?.includes('product') && parts?.includes('addtocart')) {
    localStorage.setItem('productPackage', 'addtocart');
    localStorage.setItem('produceId', parts[parts.length - 1]);
    localStorage.setItem('productPurchaseId', parts[parts.length - 2]);
  }
  if (parts?.includes('order_invoice')) {
    localStorage.setItem('searchByInvoiceOrder', parts[parts.length - 1]);
  }
  if (parts?.includes('unsubscribe-email')) {
    localStorage.setItem('unsubscribe', parts[parts.length - 1]);
  }
}

const getDefaultHomeUrl = (user, isMobile, defaultUrl) => {
  if (user && isMobile && (isStudent(user) || isParent(user))) {
    return `/${user.roleMenuFlag[0]}${defaultUrl}`;
  }
  return defaultUrl;
};

export const useInitialNewUrl = () => {
  const { user } = useAuthUser();
  const { isMobile } = useMobileSettings();
  const { parts, slug } = getCurrentUrlParts();
  const defaultUrl = getDefaultHomeUrl(user, isMobile, initialUrl);

  return resolveInitialNewUrl({
    defaultUrl,
    user,
    isMobile,
    slug,
    parts,
  });
};

const filterTournamentAddonRoutes = (routes, hasTournamentAddon) =>
  routes.filter(
    (route) =>
      !route.requiresTournamentAddon ||
      (route.requiresTournamentAddon && hasTournamentAddon),
  );

const useAuthorizedStructure = (checkMissedPayment) => {
  const { CheckAdddonsSettings, loading } = useAddonsSettings(); //NOSONAR
  const { user } = useAuthUser(); //NOSONAR
  const { isMobile } = useMobileSettings(); //NOSONAR
  const { rolePermissions } = useRolePermissions();
  if (loading || !user) {
    return {
      fallbackPath: '/signin',
      routes: [
        {
          path: '*',
          element: null,
        },
      ],
    };
  }
  const hasTournamentAddon = CheckAdddonsSettings?.includes(tournamentAddon);

  useApplicationSettings(); //NOSONAR
  if (!user) {
    return {
      fallbackPath: '/signin',
      routes: filterTournamentAddonRoutes([...authRouteConfig], hasTournamentAddon),
    };
  }
  if (isSuperAdmin(user) || isAdmin(user)) {
    const updatedAdminPages = adminPagesConfigs.map((route) => ({
      ...route,
      permittedRole: user.role,
    }));
    return {
      fallbackPath: '/signin',
      unAuthorizedComponent: <Error403 />,
      routes: filterTournamentAddonRoutes([...updatedAdminPages], hasTournamentAddon),
    };
  } else if (isStudent(user)) {
    const studentPagesConfigsData = studentPagesConfigs(
      checkMissedPayment,
      user.roleMenuFlag[0],
      isMobile,
    );
    const updatedStudentPages = studentPagesConfigsData.map((route) => ({
      ...route,
      permittedRole: user.role,
    }));
    return {
      fallbackPath: '/signin',
      unAuthorizedComponent: <Error403 />,
      routes: filterTournamentAddonRoutes([...updatedStudentPages], hasTournamentAddon),
    };
  } else if (isParent(user)) {
    const parentPagesConfigsData = parentPagesConfigs(
      checkMissedPayment,
      user.roleMenuFlag[0],
      isMobile,
    );
    const updatedParentPages = parentPagesConfigsData.map((route) => ({
      ...route,
      permittedRole: user.role,
    }));
    return {
      fallbackPath: '/signin',
      unAuthorizedComponent: <Error403 />,
      routes: filterTournamentAddonRoutes([...updatedParentPages], hasTournamentAddon),
    };
  } else if (isCoach(user) || isTournamentCoach(user)) {
    const coachPages = coachPagesConfigs(rolePermissions);
    const updatedCoachPages = coachPages.map((route) => ({
      ...route,
      permittedRole: user.role,
    }));
    return {
      fallbackPath: '/signin',
      unAuthorizedComponent: <Error403 />,
      routes: filterTournamentAddonRoutes([...updatedCoachPages], hasTournamentAddon),
    };
  } else {
    return {
      fallbackPath: '/signin',
      unAuthorizedComponent: <Error403 />,
      routes: [],
    };
  }
};

const authorizedStructures = {
  fallbackPath: '/signin',
  unAuthorizedComponent: <Error403 />,
  routes: [],
};

const useUnAuthorizedStructure = () => {
  const { CheckAdddonsSettings, loading } = useAddonsSettings();
  const fallbackPath = useInitialNewUrl();

  if (loading) {
    return {
      fallbackPath: '/signin',
      routes: [
        {
          path: '*',
          element: null,
        },
      ],
    };
  }
  const hasTournamentAddon = CheckAdddonsSettings?.includes(tournamentAddon);

  return {
    fallbackPath,
    routes: filterTournamentAddonRoutes([...authRouteConfig], hasTournamentAddon),
  };
};

const anonymousStructure = {
  routes: errorPagesConfigs.concat([
    {
      path: '/',
      element: <Navigate to={initialUrl} />,
    },
    {
      path: '*',
      element: <Navigate to='/signin' />,
    },
  ]),
};

export {
  useAuthorizedStructure,
  useUnAuthorizedStructure,
  anonymousStructure,
  authorizedStructures,
};
